/* eslint-disable react/prop-types */
import { useState } from "react";
import { gql, useApolloClient } from "@apollo/client";
import { Alert, Box, Card, CardContent, Checkbox, Chip, FormControlLabel, Stack, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import SoftButton from "components/SoftButton";
import { Campo } from "./Formulario";

export default function DatosEmpresa({ options, onRefresh, report }) {
  const client = useApolloClient();
  const puedeInscripciones = options.autoridad && options.puedeInscripciones;
  const puedeVehiculos = options.autoridad && options.puedeEditarEmpresa;
  const puedeHabilitaciones = options.autoridad && options.puedeHabilitaciones;
  const first =
    options.empresas.find(
      (e) => (options.autoridad || e.propia) && e.actividad.includes("Transportista")
    ) || options.empresas.find((e) => options.autoridad || e.propia);
  const firstActivity = first?.actividad.includes("Transportista")
    ? "Transportista"
    : first?.actividad[0] || "Generador";
  const firstInscripcion = options.inscripciones.find(
    (i) => String(i.idEmpresa) === String(first?.id) && i.actividad === firstActivity
  );
  const [idEmpresa, setIdEmpresa] = useState(String(first?.id || ""));
  const [actividad, setActividad] = useState(firstActivity);
  const [caa, setCaa] = useState(firstInscripcion?.caa || "");
  const [vencimiento, setVencimiento] = useState(firstInscripcion?.vencimiento || "");
  const [idVehiculo, setIdVehiculo] = useState("");
  const [vehiculo, setVehiculo] = useState({});
  const [nuevo, setNuevo] = useState({ tipo: "", marcaModelo: "", dominio: "" });
  const [busy, setBusy] = useState(false);
  const empresas = options.empresas.filter((e) => options.autoridad || e.propia);
  const empresa = empresas.find((e) => String(e.id) === idEmpresa);
  const vehiculos = options.vehiculos.filter((v) => String(v.idEmpresa) === idEmpresa);
  const selectInscripcion = (id, rol) => {
    const row = options.inscripciones.find(
      (i) => String(i.idEmpresa) === id && i.actividad === rol
    );
    setIdEmpresa(id);
    setActividad(rol);
    setCaa(row?.caa || "");
    setVencimiento(row?.vencimiento || "");
    setIdVehiculo("");
    setVehiculo({});
  };
  const save = async (mutation, input) => {
    setBusy(true);
    try {
      await client.mutate({ mutation, variables: { input } });
      await onRefresh();
      report("Datos guardados", "success");
      return true;
    } catch (e) {
      report(e.message, "error");
      return false;
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack
      spacing={3}
      sx={{
        "& .MuiTypography-h6": { color: "dark.main", fontWeight: 700 },
        "& .MuiTypography-subtitle1": { color: "dark.main", fontWeight: 600 },
        "& .MuiButton-root": { textTransform: "none", minHeight: 44 },
      }}
    >
      <Card>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={2}>
            <Typography variant="h6">{options.autoridad ? "Datos de empresas para manifiestos" : "Datos de mi empresa para manifiestos"}</Typography>
            <Box sx={{ maxWidth: 680 }}>
              <Campo
                label="Empresa"
                autocomplete
                value={idEmpresa}
                onChange={(id) =>
                  selectInscripcion(
                    id,
                    options.empresas.find((e) => String(e.id) === id)?.actividad[0] || "Generador"
                  )
                }
                options={empresas.map((e) => ({ id: String(e.id), label: e.razonSocial }))}
              />
            </Box>
            {empresa && (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 2fr" },
                    gap: 2,
                    p: 2,
                    bgcolor: "grey.100",
                    borderRadius: 2,
                  }}
                >
                  {[
                    ["Legajo", empresa.id],
                    ["CUIT", empresa.cuit],
                    ["Domicilio", empresa.domicilio],
                  ].map(([label, value]) => (
                    <Box key={label}>
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography variant="body2" color="dark.main">
                        {value || "Pendiente"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography variant="subtitle1">Certificado de Aptitud Ambiental</Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1.3fr 0.8fr" },
                    gap: 2,
                  }}
                >
                  <Campo
                    label="Actividad"
                    value={actividad}
                    onChange={(rol) => selectInscripcion(idEmpresa, rol)}
                    options={empresa.actividad.map((rol) => ({ id: rol, label: rol }))}
                  />
                  <Campo readOnly={!puedeInscripciones} label="Número de C.A.A." value={caa} onChange={setCaa} />
                  <Campo
                    readOnly={!puedeInscripciones}
                    label="Vencimiento del C.A.A."
                    type="date"
                    value={vencimiento}
                    onChange={setVencimiento}
                  />
                </Box>
                {puedeInscripciones && <SoftButton
                  variant="gradient"
                  color="primary"
                  startIcon={<SaveIcon />}
                  sx={{ alignSelf: "flex-end", width: { xs: "100%", sm: "fit-content" } }}
                  disabled={busy || !empresa.actividad.includes(actividad) || !caa || !vencimiento}
                  onClick={() =>
                    save(
                      gql`
                        mutation ($input: InscripcionManifiestoInput!) {
                          guardarInscripcionManifiesto(input: $input)
                        }
                      `,
                      { idEmpresa, actividad, caa, vencimiento }
                    )
                  }
                >
                  Guardar C.A.A.
                </SoftButton>}
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
      {empresa?.actividad.includes("Transportista") && (
        <Card>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              <Typography variant="h6">Vehículos de la transportista</Typography>
              {puedeVehiculos && <>
              <Alert severity="info">
                Se reutilizan tipo y dominio ya registrados. Al modificar estos datos, el vehículo
                queda pendiente de habilitación por la autoridad.
              </Alert>
              <Typography variant="subtitle1">Agregar vehículo</Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr 1fr" },
                  gap: 2,
                }}
              >
                {[
                  ["tipo", "Tipo"],
                  ["marcaModelo", "Marca y modelo"],
                  ["dominio", "Dominio"],
                ].map(([key, label]) => (
                  <Campo
                    key={key}
                    label={label}
                    value={nuevo[key]}
                    onChange={(value) => setNuevo({ ...nuevo, [key]: value })}
                  />
                ))}
              </Box>
              <SoftButton
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                sx={{ alignSelf: "flex-end", width: { xs: "100%", sm: "fit-content" } }}
                disabled={busy || !nuevo.tipo || !nuevo.marcaModelo || !nuevo.dominio}
                onClick={async () => {
                  const saved = await save(
                    gql`
                      mutation ($input: EmpresaInput!) {
                        saveEmpresa(input: $input) {
                          id
                        }
                      }
                    `,
                    {
                      id: idEmpresa,
                      vehiculos: [
                        ...vehiculos.map(({ id, tipo, marcaModelo, dominio, tallerPropio }) => ({
                          id,
                          tipo,
                          marcaModelo,
                          dominio,
                          tallerPropio,
                        })),
                        { ...nuevo, tallerPropio: false },
                      ],
                    }
                  );
                  if (saved) setNuevo({ tipo: "", marcaModelo: "", dominio: "" });
                }}
              >
                Agregar vehículo
              </SoftButton>
              </>}
              <Typography variant="subtitle1" sx={{ borderTop: 1, borderColor: "divider", pt: 3 }}>
                Datos de habilitación y seguro
              </Typography>
              <Box sx={{ maxWidth: 680 }}>
                <Campo
                  label="Vehículo"
                  value={idVehiculo}
                  onChange={(id) => {
                    const selected = vehiculos.find((v) => String(v.id) === id);
                    setIdVehiculo(id);
                    setVehiculo(selected || {});
                  }}
                  options={vehiculos.map((v) => ({
                    id: String(v.id),
                    label: `${v.tipo} · ${v.dominio}`,
                  }))}
                />
              </Box>
              {idVehiculo && (
                <>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 2 }}>
                    {[["tipo", "Tipo"], ["marcaModelo", "Marca y modelo"], ["dominio", "Dominio"]].map(([key, label]) => (
                      <Campo key={key} label={label} value={vehiculo[key] || ""} readOnly />
                    ))}
                  </Box>
                  <Chip
                    sx={{ alignSelf: "flex-start" }}
                    variant="outlined"
                    color={vehiculo.habilitado ? "success" : "warning"}
                    label={
                      vehiculo.habilitado
                        ? "Habilitado por la autoridad"
                        : "Pendiente de habilitación"
                    }
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
                      gap: 2,
                    }}
                  >
                    {[
                      ["habilitacion", "Número de habilitación"],
                      ["vencimientoHabilitacion", "Vencimiento de habilitación"],
                      ["poliza", "Póliza de seguro"],
                      ["vencimientoSeguro", "Vencimiento del seguro"],
                    ].map(([key, label]) => (
                      <Campo
                        key={key}
                        readOnly={!puedeHabilitaciones}
                        label={label}
                        type={key.startsWith("vencimiento") ? "date" : "text"}
                        value={vehiculo[key] || ""}
                        onChange={(value) => setVehiculo({ ...vehiculo, [key]: value })}
                      />
                    ))}
                  </Box>
                  {puedeHabilitaciones && <FormControlLabel label="Habilitar vehículo" control={
                    <Checkbox checked={Boolean(vehiculo.habilitado)} onChange={(_, checked) => setVehiculo({ ...vehiculo, habilitado: checked })} />
                  } />}
                  {puedeHabilitaciones && <SoftButton
                    variant="gradient"
                    color="primary"
                    startIcon={<SaveIcon />}
                    sx={{ alignSelf: "flex-end", width: { xs: "100%", sm: "fit-content" } }}
                    disabled={busy}
                    onClick={async () => {
                      await save(
                        gql`
                          mutation ($input: VehiculoHabilitacionInput!) {
                            guardarHabilitacionManifiesto(input: $input)
                          }
                        `,
                        {
                          id: idVehiculo,
                          habilitado: Boolean(vehiculo.habilitado),
                          habilitacion: vehiculo.habilitacion || "",
                          vencimientoHabilitacion: vehiculo.vencimientoHabilitacion || null,
                          poliza: vehiculo.poliza || "",
                          vencimientoSeguro: vehiculo.vencimientoSeguro || null,
                        }
                      );

                    }}
                  >
                    Guardar datos del vehículo
                  </SoftButton>}
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
